import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Search, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CatalogLookup() {
  const navigate = useNavigate();
  const [catalogInput, setCatalogInput] = useState('');
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLookup = async () => {
    if (!catalogInput.trim()) { toast.error('Enter a catalog number'); return; }
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/products/catalog-lookup`, { params: { catalog_number: catalogInput.trim() } });
      setResults(data.results || []);
      if (data.results.length === 0) toast.info('No product found');
    } catch (e) {
      if (e.response?.status === 404) {
        setResults([]);
        toast.error('No product found with this catalog number');
      } else {
        toast.error('Lookup failed');
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F6F9] p-4">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#1a1a2e] tracking-tight">Catalog MRP Lookup</h1>
            <p className="text-xs text-muted-foreground mt-0.5">Enter company catalog number to get instant MRP & product details</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
            <ArrowLeft size={14} className="mr-1" /> Dashboard
          </Button>
        </div>

        {/* Search Box */}
        <div className="bg-white p-4 border border-border rounded-sm shadow-sm">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Catalog Number</label>
              <Input
                value={catalogInput}
                onChange={(e) => setCatalogInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLookup(); }}
                placeholder="e.g. 21011, 101751WH, 681100"
                className="mt-1 h-10 text-lg font-mono tracking-wider"
                autoFocus
                data-testid="catalog-input"
              />
            </div>
            <Button onClick={handleLookup} disabled={loading} className="h-10 px-6 bg-[#0066FF] hover:bg-[#0052CC] text-white" data-testid="catalog-search-btn">
              <Search size={16} className="mr-1.5" /> {loading ? 'Searching...' : 'Search'}
            </Button>
          </div>
        </div>

        {/* Results */}
        {searched && (
          <div className="bg-white border border-border rounded-sm shadow-sm overflow-hidden">
            {results.length > 0 ? (
              <>
                {/* Big MRP Display for first result */}
                <div className="p-5 border-b border-border bg-gradient-to-r from-[#1a1a2e] to-[#2d2d5e]">
                  <div className="text-center">
                    <div className="text-xs text-gray-400 uppercase tracking-widest mb-1">MRP</div>
                    <div className="text-5xl font-bold text-white tracking-tight" data-testid="mrp-display">
                      {results[0].mrp?.toFixed(2) || '0.00'}
                    </div>
                    <div className="text-sm text-gray-300 mt-2 font-medium" data-testid="product-name-display">
                      {results[0].description} — {results[0].brand} {results[0].type}
                    </div>
                  </div>
                </div>

                {/* Details Table */}
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F0F2F5]">
                      <TableHead className="text-[10px] font-semibold">Code</TableHead>
                      <TableHead className="text-[10px] font-semibold">Cat. No.</TableHead>
                      <TableHead className="text-[10px] font-semibold">Description</TableHead>
                      <TableHead className="text-[10px] font-semibold">Brand</TableHead>
                      <TableHead className="text-[10px] font-semibold">Sub Brand</TableHead>
                      <TableHead className="text-[10px] font-semibold">Type</TableHead>
                      <TableHead className="text-[10px] font-semibold">Color</TableHead>
                      <TableHead className="text-[10px] font-semibold">Category</TableHead>
                      <TableHead className="text-[10px] font-semibold">Unit</TableHead>
                      <TableHead className="text-[10px] font-semibold text-right">MRP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((p, i) => (
                      <TableRow key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F8F9FB]'}>
                        <TableCell className="text-xs font-mono font-semibold text-[#0066FF]">{p.code}</TableCell>
                        <TableCell className="text-xs font-mono">{p.catalog_number || '-'}</TableCell>
                        <TableCell className="text-xs font-medium">{p.description}</TableCell>
                        <TableCell className="text-xs">{p.brand}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{p.type || '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{p.switch_subtype || '-'}</TableCell>
                        <TableCell className="text-xs">{p.color || '-'}</TableCell>
                        <TableCell>
                          <span className={`px-1 py-0.5 rounded text-[9px] font-semibold ${
                            p.product === 'SWITCH' ? 'bg-blue-100 text-blue-700' :
                            p.product === 'PLATE' ? 'bg-amber-100 text-amber-700' :
                            p.product === 'WIRE' || p.product === 'PROJECT WIRE' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>{p.product}</span>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{p.unit || 'NOS'}</TableCell>
                        <TableCell className="text-xs font-bold text-right">{p.mrp?.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {results.length > 1 && (
                  <div className="px-3 py-2 text-[10px] text-muted-foreground border-t bg-[#F8F9FB]">
                    Found {results.length} products matching this catalog number
                  </div>
                )}
              </>
            ) : (
              <div className="p-10 text-center text-muted-foreground text-sm" data-testid="no-results">
                No product found for catalog number "<span className="font-mono font-bold">{catalogInput}</span>"
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
